import { useStorage } from "@vueuse/core";
import { isEmpty } from "@/lib/utils.ts";

export type ExpirableRecord = {
  expires_at: string;
};

type ExpirableObjectValue<T extends Record<string, unknown>> = T &
  ExpirableRecord;

type ExpirablePrimitiveValue<T> = {
  data: T;
} & ExpirableRecord;

export type ExpirableValue<T> =
  T extends Record<string, unknown>
    ? ExpirableObjectValue<T>
    : ExpirablePrimitiveValue<T>;

const getExpirationDate = (daysFromNow: number) => {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + daysFromNow);

  return expirationDate.toISOString();
};

export const useExpirableStorage = <T>(
  key: string,
  initialValue: T | null = null,
  expireDays: number = 7,
) => {
  const storage = useStorage<ExpirableValue<T> | null>(
    key,
    {} as ExpirableValue<T>,
    undefined,
    {
      serializer: {
        read: (v) => {
          try {
            return JSON.parse(v);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (e) {
            return v as unknown as ExpirableValue<T> | null;
          }
        },
        write: (v) => JSON.stringify(v),
      },
    },
  );

  const migrateLegacyValue = () => {
    const value = storage.value as (ExpirableValue<T> | T | null | boolean) & {
      data?: T;
      expires_at: string;
    };

    if (isEmpty(value) || !(value === false || value) || value.expires_at) {
      return;
    }

    storage.value = {
      data:
        typeof value === "object" && value.data !== undefined
          ? value.data
          : (value as T),
      expires_at: getExpirationDate(1),
    } as ExpirableValue<T>;
  };

  migrateLegacyValue();

  const isValid = (
    data: ExpirableValue<T> | null | undefined = storage.value,
  ) => !!data && data.expires_at > new Date().toISOString();

  const set = (data: T, days: number | undefined = undefined) => {
    storage.value = {
      data,
      expires_at: getExpirationDate(days || expireDays),
    } as ExpirableValue<T>;
  };

  const get = () =>
    isValid(storage.value) ? storage.value?.data : initialValue;

  const remove = () => {
    storage.value = null;
    localStorage.removeItem(key);
  };

  return {
    set,
    get,
    remove,
  };
};
